import { useCallback, useEffect, useMemo, useState } from "react";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getPaymentOptionTooltipText } from "@payment-ui/helpers/MorePaymentOptions";
import { getAmazonPayDelinkedMessageFreqCappPayload } from "@payment-ui/components/payment-method/wallet/constants";
// types
import { PaymentGroupDataDef } from "@payment-ui/interfaces/Payment";
//hooks
import { useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";
import { MorePaymentOptionsOtherProps } from "@payment-ui/interfaces/MorePaymentOptions";

export const useTooltip = ({
    paymentGroupData,
    otherProps,
    inViewPort,
    onViewTooltip,
}: {
    paymentGroupData: PaymentGroupDataDef;
    otherProps?: MorePaymentOptionsOtherProps;
    inViewPort: boolean;
    onViewTooltip?: (groupName: string, text: string) => void;
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const { amazonPaySdkStateData } = otherProps ?? {};
    const { isNewSdkAvailable, linked, wasPreviouslyLinkedUsingSDK } = amazonPaySdkStateData ?? {};

    const { shouldProceed: shouldRenderAmazonPayDelinkedMessage } = useLocalStorageBasedFrequencyCapping(
        getAmazonPayDelinkedMessageFreqCappPayload(),
    );

    const tooltipText = useMemo(() => {
        return getPaymentOptionTooltipText(paymentGroupData);
    }, [paymentGroupData]);

    const hideTooltip = useCallback(() => {
        setShowTooltip(false);
    }, []);

    useEffect(() => {
        if (!inViewPort) {
            return;
        }

        if (isEmpty(tooltipText)) {
            setShowTooltip(false);
            return;
        }

        // extend for other payment groups here
        if (
            isNewSdkAvailable &&
            !linked &&
            wasPreviouslyLinkedUsingSDK === true &&
            shouldRenderAmazonPayDelinkedMessage()
        ) {
            setShowTooltip(true);
            onViewTooltip?.(paymentGroupData.groupName, tooltipText);
        }

        return () => {
            setShowTooltip(false);
        };
    }, [
        tooltipText,
        shouldRenderAmazonPayDelinkedMessage,
        isNewSdkAvailable,
        linked,
        wasPreviouslyLinkedUsingSDK,
        inViewPort,
        onViewTooltip,
        paymentGroupData.groupName,
    ]);

    return {
        tooltipText,
        showTooltip,
        hideTooltip,
    };
};
